import { useState, useEffect } from 'react';
import {
  Loader2, CheckCircle, AlertCircle, XCircle, Clock,
  RefreshCw, ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';


const STATUS_CFG = {
  success:     { colour: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: CheckCircle },
  partial:     { colour: 'text-amber-700 bg-amber-50 border-amber-200',       icon: AlertCircle },
  failed:      { colour: 'text-red-600 bg-red-50 border-red-200',             icon: XCircle },
  in_progress: { colour: 'text-blue-600 bg-blue-50 border-blue-200',          icon: Loader2 },
};


function RunRow({ run }) {
  const [expanded, setExpanded] = useState(false);
  const cfg  = STATUS_CFG[run.status] || STATUS_CFG.failed;
  const Icon = cfg.icon;
  const hasDetail = run.error_message || run.http_status_code || run.response_time_ms;

  const durationStr = run.fetch_completed_at && run.fetch_started_at
    ? `${Math.round((new Date(run.fetch_completed_at) - new Date(run.fetch_started_at)) / 100) / 10}s`
    : null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <button onClick={() => hasDetail && setExpanded(!expanded)}
        className={`w-full flex items-center gap-3 p-3 text-left ${hasDetail ? 'hover:bg-slate-50' : 'cursor-default'} transition-colors`}>
        {hasDetail ? (
          expanded
            ? <ChevronDown size={14} className="text-slate-400 shrink-0" />
            : <ChevronRight size={14} className="text-slate-400 shrink-0" />
        ) : <span className="w-3.5" />}

        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${cfg.colour} shrink-0`}>
          <Icon size={10} className={run.status === 'in_progress' ? 'animate-spin' : ''} />
          {run.status}
        </span>

        <div className="flex-1 min-w-0 flex items-center gap-3 text-xs flex-wrap">
          <span className="text-slate-600">
            {run.items_found || 0} found, <span className="text-slate-900 font-medium">{run.new_items_created || 0} new</span>
          </span>
          {durationStr && <span className="text-slate-400">{durationStr}</span>}
          <span className="text-[10px] uppercase tracking-wider text-slate-400">{run.triggered_by}</span>
        </div>

        <span className="text-xs text-slate-400 shrink-0">
          {new Date(run.fetch_started_at).toLocaleString()}
        </span>
      </button>

      {expanded && hasDetail && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-200 text-xs space-y-1 ml-8">
          {run.http_status_code && (
            <p className="text-slate-500">HTTP <span className="text-slate-900 font-mono">{run.http_status_code}</span></p>
          )}
          {run.response_time_ms && (
            <p className="text-slate-500">Response time <span className="text-slate-900">{run.response_time_ms} ms</span></p>
          )}
          {run.error_message && (
            <div className="mt-2 bg-red-50 border border-red-200 rounded p-2">
              <p className="text-[10px] uppercase tracking-wider text-red-600 font-semibold mb-0.5">Error</p>
              <p className="text-xs text-red-600 font-mono break-words">{run.error_message}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function SourceFetchRunsTab({ sourceId }) {
  const [runs, setRuns]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [sourceId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/feeds/sources/${sourceId}/runs`, { params: { limit: 50 } });
      setRuns(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-slate-400">
          Last {runs.length} fetch {runs.length === 1 ? 'run' : 'runs'}
        </p>
        <button onClick={load}
          className="flex items-center gap-1 text-slate-400 hover:text-slate-700 text-xs transition-colors">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-slate-400 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading runs...
        </div>
      ) : runs.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">
          No fetch runs yet. Click "Fetch now" above to ingest items.
        </div>
      ) : (
        <div className="space-y-2">
          {runs.map(run => <RunRow key={run.id} run={run} />)}
        </div>
      )}
    </div>
  );
}